import { useAuthState } from '@auth/hooks';
import { CommunityAuthRequired } from '@communities/community/components/CommunityAuthRequired.tsx';
import { AddCommunityGroupDialog, CommunityGroupItemGrid } from '@communities/community-groups/components';
import { useCommunityGroups } from '@communities/community-groups/hooks';
import { routes } from '@communities/routes';
import { formatErrorMessages } from '@core/adapters/errors/transformError';
import { CommunityHeader, ErrorDisplay, HeroBackground } from '@core/components';
import { CardGrid } from '@core/components/CardGrid';
import { track } from '@core/logging/appInsightsIntegration';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Section, Heading, Description, Button, ButtonType, ButtonSize, OverlayMode, Shimmer } from '@soteria/design-system-ve';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import * as styles from './CommunityGroupsPage.css.ts';

/**
 * Community Groups List Page Component
 *
 * Displays a list of community groups
 */
function CommunityGroupsPageComponent() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthState();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const { data, loading, errors, refetch } = useCommunityGroups();

  // Check if user is system admin using Redux selector
  const { isUserSystemAdmin } = useAuthState();

  // Show error dialog when an error occurs
  useEffect(() => {
    if (errors) {
      setShowErrorDialog(true);
    }
  }, [errors]);

  // Create a map of community group IDs to community counts
  const communityCounts: Record<string, number> = {};

  data.forEach((group: { id: string }) => {
    communityCounts[group.id] = 0; // Default to 0 communities
  });

  // Use group navigation handler
  const handleGroupClick = (groupId: string) => {
    navigate(routes.communityGroups.detail(groupId));
  };

  // Handle authentication check
  if (!isAuthenticated) {
    // Track access denied event
    track('community_access_denied', {
      resource: 'community_groups_list',
      reason: 'not_authenticated',
      path: window.location.pathname,
    });

    return <CommunityAuthRequired />;
  }

  // Handle retry action
  const handleRetry = () => {
    setShowErrorDialog(false);
    refetch();
  };

  // Action button for header (only for system administrators)
  const headerActions = isUserSystemAdmin ? (
    <Button buttonSize={ButtonSize.Large} buttonType={ButtonType.Brand} overlayMode={OverlayMode.Solid} onClick={() => setIsCreateDialogOpen(true)}>
      ADD COMMUNITY GROUP
    </Button>
  ) : undefined;

  // V1 Header component
  const v1Header = (
    <div className={styles.v1HeaderWrapper}>
      <Section spacing="medium">
        <Heading level="h1">Community Groups</Heading>
        <Description>Browse and manage community groups</Description>
      </Section>

      {/* Add Button Section - Only visible to system administrators */}
      {isUserSystemAdmin && (
        <Section spacing="xlarge">
          <Button buttonSize={ButtonSize.Large} buttonType={ButtonType.Brand} overlayMode={OverlayMode.Solid} onClick={() => setIsCreateDialogOpen(true)}>
            ADD COMMUNITY GROUP
          </Button>
        </Section>
      )}
    </div>
  );

  return (
    <>
      <HeroBackground />

      {/* Header with V1/V2 switching via wrapper */}
      <CommunityHeader
        v1Component={v1Header}
        v2Children={headerActions}
        v2Props={{
          title: 'Community Groups',
          subtitle: 'Browse and manage community groups',
          'data-testid': 'community-groups-header-v2',
        }}
      />

      <div className={styles.contentWrapper}>
        {loading ? (
          <Section spacing="large">
            <CardGrid columns="auto-fit" gap="medium">
              {Array.from({ length: 6 }, (_, index) => `shimmer-${index}`).map(key => (
                <Shimmer
                  key={key}
                  label="Loading community group"
                  style={{
                    minHeight: '120px',
                    width: '100%',
                    borderRadius: '8px',
                  }}
                />
              ))}
            </CardGrid>
          </Section>
        ) : (
          <Section spacing="large">
            {data.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No community groups found.</p>
                {isUserSystemAdmin && <p>Click the "Add Community Group" button to create a new group.</p>}
              </div>
            ) : (
              <CommunityGroupItemGrid communityGroups={data} onGroupClick={handleGroupClick} />
            )}
          </Section>
        )}
      </div>

      {/* Create Community Group Dialog */}
      <AddCommunityGroupDialog isOpen={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} />

      {/* Error Dialog */}
      {errors && (
        <ErrorDisplay
          isOpen={showErrorDialog}
          message={formatErrorMessages(errors)}
          size="md"
          title="Error Loading Community Groups"
          onClose={() => setShowErrorDialog(false)}
          onRetry={handleRetry}
        />
      )}
    </>
  );
}

export const CommunityGroupsPage = withAppInsightsTracking(CommunityGroupsPageComponent, 'CommunityGroupsPage');
